import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import getSymbolFromCurrency from 'currency-symbol-map';
import {
  FaSearch,
  FaFilter,
  FaStar,
  FaShoppingCart,
  FaHeart,
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import data from "../Data.json";
import { useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { useCurrency } from "../components/CurrencyContext";

// Add this mapping to your component
const COMPANY_ID_MAP = {
  Latafa: 4,
  "Moltan Brown": 1,
  Coola: 2,
  Aesop: 3
};

const Products = ({ showHomePage = false }) => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("all");

  // State Management
  const [isApiLoading, setIsApiLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [filters, setFilters] = useState({
    search: "",
    category: "all",
    priceRange: [0, 100000],
    sort: "",
    showDiscount: false,
    brand: "all",
    rating: 0,
    stockOnly: false,
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [wishlist, setWishlist] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { rate, currency } = useCurrency();


    const convertPrice = (price) => {
      const productPrice = parseFloat(price) || 0;
      const convertedPrice = productPrice * rate;
      return convertedPrice
    }
    const currencySymbol = currency === "SAR" ? "﷼" : getSymbolFromCurrency(currency) || currency;    
    

  // Products per page for pagination
  const PRODUCTS_PER_PAGE = 6;

  const fetchProductsByCompany = useCallback(async (brandId) => {
    try {
      setIsApiLoading(true);

      // Use the company ID to make the API call
      const response = await axios.get(
        `${process.env.REACT_APP_API_URI}/products/brand/${brandId}`
      );

      if (response.status === 200) {
        localStorage.setItem("apiProducts", JSON.stringify(response.data));
        return response.data;
      }
      return null;
    } catch (error) {
      console.error(
        "Error fetching products for company ID:",
        brandId,
        error
      );
      return null;
    } finally {
      setIsApiLoading(false);
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      setIsApiLoading(true);

      // If you have an API endpoint for all products, use that
      // Example: const response = await axios.get(`${process.env.REACT_APP_API_URI}/products`);

      // If not, you can fetch from the first company as a default
      const firstCompany = companies.length > 0 ? companies[0].brand_id : 4;
      const response = await axios.get(
        `${process.env.REACT_APP_API_URI}/products/brand/${firstCompany}`
      );

      if (response.status === 200) {
        localStorage.setItem("apiProducts", JSON.stringify(response.data));
        return response.data;
      }
      return null;
    } catch (error) {
      console.error("Error fetching all products:", error);
      return null;
    } finally {
      setIsApiLoading(false);
    }
  }, [companies]);

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URI}/brand/company/${65}` //prefix compnay id vital Source
      );
      if (response.status === 200) {
        setCompanies(response.data);
        return response.data;
      }
      return [];
    } catch (error) {
      console.error("Error fetching companies:", error);
      return [];
    }
  }, []);

  // Load and merge data
  useEffect(() => {
    const getCompanyFromUrl = () => {
      // Check URL params or search params for company info
      const searchParams = new URLSearchParams(location.search);
      const companyParam = searchParams.get("brand");

      if (companyParam && COMPANY_ID_MAP[companyParam]) {
        return COMPANY_ID_MAP[companyParam];
      }

      // Default company ID if none found
      return 4; // vital Source as default
    };

    const initializeData = async () => {
      setIsLoading(true);

      try {
        const companiesList = await fetchCompanies();

        // Get company from URL or default to "all"
        const searchParams = new URLSearchParams(location.search);
        const companyParam = searchParams.get("id");

        let companyId = null;

        if (
          companyParam &&
          companiesList.some((c) => c.brand_id.toString() === companyParam)
        ) {
          companyId = companyParam;
          setSelectedCompany(companyParam);
        } else {
          setSelectedCompany("all");
        }
        let apiData = null;

        // Try to use stored API products first
        if (companyId) {
          apiData = await fetchProductsByCompany(companyId);
        } else {
          // Fetch all products (you might need a new API endpoint for this)
          // Or fetch from first company as default
          const firstCompany =
            companiesList.length > 0 ? companiesList[0].brand_id : 4;
          apiData = await fetchProductsByCompany(firstCompany);
        }

        // If we have API data, merge with local images
        if (apiData && apiData.length > 0) {
          // Merge API data with JSON images
          const mergedProducts = apiData.map((apiProduct) => {
            const matchingProduct = data.products.find(
              (p) => p.id === apiProduct.id
            );
            return {
              ...apiProduct,
              image: apiProduct?.image || data.products[0].image, // Fallback to first image if no match
              // Ensure consistent data structure
              rating: apiProduct.rating || matchingProduct?.rating || 4,
              in_stock:
                apiProduct.in_stock !== undefined ? apiProduct.in_stock : true,
              brand: apiProduct.brand || matchingProduct?.brand || "BrandA",
              category:
                apiProduct.category || matchingProduct?.category || "featured",
            };
          });

          setProducts(mergedProducts);
        } else {
          // Last resort fallback to JSON data
          setProducts(data.products);
        }
      } catch (error) {
        console.error("Error loading products:", error);
        setProducts(data.products); // Fallback to JSON data on error
      } finally {
        setIsLoading(false);
      }
    };

    initializeData();

    // Load wishlist from localStorage if exists
    const storedWishlist = localStorage.getItem("wishlist");
    if (storedWishlist) {
      setWishlist(JSON.parse(storedWishlist));
    }
  }, [location.search, fetchProductsByCompany]);

  // Memoized filter options - derived from actual products for dynamic filtering
  const filterOptions = useMemo(() => {
    // Extract unique values from products
    const categories = [
      "all",
      ...new Set(products.map((p) => p.category).filter(Boolean)),
    ];
    const brands = [
      "all",
      ...new Set(products.map((p) => p.brand).filter(Boolean)),
    ];

    return {
      categories,
      brands,
      ratings: [
        { value: 0, label: "All Ratings" },
        { value: 4, label: "4 Stars & Up" },
        { value: 3, label: "3 Stars & Up" },
        { value: 2, label: "2 Stars & Up" },
      ],
    };
  }, [products]);

  // Toggle wishlist item
  const toggleWishlist = useCallback((e, productId) => {
    e.stopPropagation(); // Prevent navigation to product detail

    setWishlist((prev) => {
      const newWishlist = prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId];

      // Save to localStorage
      localStorage.setItem("wishlist", JSON.stringify(newWishlist));
      return newWishlist;
    });
  }, []);

  const applyCurrentFilters = useCallback(
    (data) => {
      let filtered = [...data];

      // Apply each active filter from the current filters state

      // Search filter
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase().trim();
        filtered = filtered.filter(
          (product) =>
            product.product_name?.toLowerCase().includes(searchTerm) ||
            product.description?.toLowerCase().includes(searchTerm)
        );
      }

      // Category filter
      if (filters.category !== "all") {
        filtered = filtered.filter(
          (product) => product.category === filters.category
        );
      }

      // Price range filter
      filtered = filtered.filter((product) => {
        const price =
          typeof product.price === "string"
            ? parseFloat(product.price.replace(/[^0-9.]/g, ""))
            : parseFloat(product.price);

        return (
          !isNaN(price) &&
          price >= filters.priceRange[0] &&
          price <= filters.priceRange[1]
        );
      });

      // Apply remaining filters (discount, brand, rating, stock)
      if (filters.showDiscount) {
        filtered = filtered.filter((product) => !!product.discount_price);
      }

      if (filters.brand !== "all") {
        filtered = filtered.filter(
          (product) => product.brand === filters.brand
        );
      }

      if (filters.rating > 0) {
        filtered = filtered.filter(
          (product) =>
            product.rating && parseFloat(product.rating) >= filters.rating
        );
      }

      if (filters.stockOnly) {
        filtered = filtered.filter((product) => product.in_stock);
      }

      // Apply sorting
      if (filters.sort) {
        filtered.sort((a, b) => {
          const priceA =
            typeof a.price === "string"
              ? parseFloat(a.price.replace(/[^0-9.]/g, ""))
              : parseFloat(a.price);

          const priceB =
            typeof b.price === "string"
              ? parseFloat(b.price.replace(/[^0-9.]/g, ""))
              : parseFloat(b.price);

          return filters.sort === "lowToHigh"
            ? priceA - priceB
            : priceB - priceA;
        });
      }

      setFilteredProducts(filtered);
    },
    [filters]
  );

  // Filter handler with optimization
  const handleFilter = useCallback(() => {
    let filtered = [...products];

    // Search filter - check both name and description
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase().trim();
      filtered = filtered.filter(
        (product) =>
          product.product_name?.toLowerCase().includes(searchTerm) ||
          product.description?.toLowerCase().includes(searchTerm)
      );
    }

    // Category filter
    if (filters.category !== "all") {
      filtered = filtered.filter(
        (product) => product.category === filters.category
      );
    }

    // Price range filter - sanitize price format
    filtered = filtered.filter((product) => {
      const price =
        typeof product.price === "string"
          ? parseFloat(product.price.replace(/[^0-9.]/g, ""))
          : parseFloat(product.price);

      return (
        !isNaN(price) &&
        price >= filters.priceRange[0] &&
        price <= filters.priceRange[1]
      );
    });

    // Discount filter
    if (filters.showDiscount) {
      filtered = filtered.filter((product) => !!product.discount_price);
    }

    // Brand filter
    if (filters.brand !== "all") {
      filtered = filtered.filter((product) => product.brand === filters.brand);
    }

    // Rating filter
    if (filters.rating > 0) {
      filtered = filtered.filter(
        (product) =>
          product.rating && parseFloat(product.rating) >= filters.rating
      );
    }

    // Stock filter
    if (filters.stockOnly) {
      filtered = filtered.filter((product) => product.in_stock);
    }

    // Sorting
    if (filters.sort) {
      filtered.sort((a, b) => {
        // Ensure numeric prices for comparison
        const priceA =
          typeof a.price === "string"
            ? parseFloat(a.price.replace(/[^0-9.]/g, ""))
            : parseFloat(a.price);

        const priceB =
          typeof b.price === "string"
            ? parseFloat(b.price.replace(/[^0-9.]/g, ""))
            : parseFloat(b.price);

        return filters.sort === "lowToHigh" ? priceA - priceB : priceB - priceA;
      });
    }

    setFilteredProducts(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [filters, products]);

  // Apply filters when dependencies change
  useEffect(() => {
    handleFilter();
  }, [handleFilter]);

  // Update filters and handle category selection
  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));

    // Special case for category to update selected category UI state
    if (key === "category") {
      setSelectedCategory(value);
    }
  }, []);

  // Reset filters
  // Update the resetFilters function
  const resetFilters = useCallback(() => {
    setFilters({
      search: "",
      category: "all",
      priceRange: [0, 100000],
      sort: "",
      showDiscount: false,
      brand: "all",
      rating: 0,
      stockOnly: false,
    });
    setSelectedCategory("all");

    // Also reset company selection and load default products
    setSelectedCompany("all");
    setIsLoading(true); // Show loading state

    const defaultCompany = companies.length > 0 ? companies[0].brand_id : 4;
    fetchProductsByCompany(defaultCompany)
      .then((data) => {
        if (data) {
          // Process and set products
          setProducts(data);
          setFilteredProducts(data);

          // Reset pagination if you have it
          if (typeof setCurrentPage === "function") {
            setCurrentPage(1);
          }

          // Apply any default sorting or processing
          const sortedData = [...data].sort((a, b) =>
            a.product_name.localeCompare(b.product_name)
          );
          setFilteredProducts(sortedData);

          setIsLoading(false);
        }
      })
      .catch((error) => {
        console.error("Error fetching default products:", error);
        setProducts([]);
        setFilteredProducts([]);
        setIsLoading(false);
      });
  }, [companies, fetchProductsByCompany]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE);
  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * PRODUCTS_PER_PAGE;
    return filteredProducts.slice(startIndex, startIndex + PRODUCTS_PER_PAGE);
  }, [filteredProducts, currentPage]);

  // Format price - ensure consistent display
  const formatPrice = (price) => {
    if (!price) return "0";

    // Handle string or number inputs
    const numPrice =
      typeof price === "string"
        ? parseFloat(price.replace(/[^0-9.]/g, ""))
        : price;

    return `${numPrice.toFixed(2)}`;
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  // Render loading state
  if (isLoading || isApiLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="p-8 rounded-2xl bg-white shadow-xl text-center">
          <div className="w-16 h-16 border-4 border-[#f36812] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-6 text-gray-700 font-medium">
            Curating your premium collection...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-[8.3rem]">
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <h1 className="text-4xl md:text-5xl font-light mb-4">
            Premium Collection
          </h1>
          <p className="text-gray-300 max-w-2xl text-lg">
            Discover our curated selection of high-quality products designed to
            elevate your lifestyle with exceptional craftsmanship and timeless
            elegance.
          </p>
        </div>
      </div>

      {/* Categories Scrollbar */}
      <div className="sticky top-0 z-40 bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center space-x-1 overflow-x-auto scrollbar-hide py-4">
            {filterOptions.categories.map((category) => (
              <button
                key={category}
                onClick={() => updateFilter("category", category)}
                className={`whitespace-nowrap px-5 py-2 text-sm rounded-none border-b-2 transition-all ${selectedCategory === category
                  ? "border-[#f36812] text-[#f36812] font-medium"
                  : "border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300"
                  }`}
              >
                {category === "all"
                  ? "All Products"
                  : category.split(/(?=[A-Z])/).join(" ")}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Filter Sidebar */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="md:w-1/4"
          >
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden sticky top-24">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold flex items-center gap-2 text-gray-800">
                    <FaFilter className="text-[#f36812]" />
                    Refine Results
                  </h2>
                  <button
                    onClick={() => setIsFilterOpen(!isFilterOpen)}
                    className="md:hidden text-gray-500 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
                  >
                    {isFilterOpen ? "−" : "+"}
                  </button>
                </div>

                <AnimatePresence>
                  {(isFilterOpen || window.innerWidth >= 768) && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="space-y-6"
                    >
                      {/* Search Input */}
                      <div className="relative">
                        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          value={filters.search}
                          onChange={(e) =>
                            updateFilter("search", e.target.value)
                          }
                          placeholder="Search products..."
                          className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#f36812]/20 focus:border-[#f36812] bg-gray-50"
                        />
                      </div>

                      {/* Price Range */}
                      <div className="space-y-3">
                        <label className="block text-sm font-medium text-gray-700">
                          Price Range
                        </label>
                        <div className="flex items-center justify-between text-sm text-gray-500">
                          <span>{filters.priceRange[0]}</span>
                          <span>{filters.priceRange[1]}</span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="100000"
                          value={filters.priceRange[1]}
                          onChange={(e) =>
                            updateFilter("priceRange", [
                              filters.priceRange[0],
                              parseInt(e.target.value),
                            ])
                          }
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#f36812]"
                        />
                      </div>

                      {/* Company Select - Add this to the filters section */}
                      <div className="space-y-3">
                        <label className="block text-sm font-medium text-gray-700">
                          Brand
                        </label>
                        <select
                          value={selectedCompany}
                          onChange={(e) => {
                            setSelectedCompany(e.target.value);
                            // Fetch products for selected company
                            if (e.target.value === "all") {
                              // Reset to show all products
                              setProducts([]); // Clear current products
                              setIsLoading(true); // Show loading state

                              // Fetch products from all companies or use default behavior
                              fetchProducts()
                                .then((data) => {
                                  if (data) {
                                    setProducts(data);
                                    setFilteredProducts(data);
                                    setIsLoading(false);
                                  }
                                })
                                .catch((error) => {
                                  console.error(
                                    "Error fetching all products:",
                                    error
                                  );
                                  setIsLoading(false);
                                });
                            } else {
                              // Fetch products for the specific company
                              setIsLoading(true);
                              fetchProductsByCompany(e.target.value)
                                .then((data) => {
                                  if (data) {
                                    // Process and set products
                                    setProducts(data);
                                    setFilteredProducts(data);
                                    setIsLoading(false);

                                    // If you have other filters applied, make sure to reapply them
                                    applyCurrentFilters(data);
                                  }
                                })
                                .catch((error) => {
                                  console.error(
                                    `Error fetching products for company ${e.target.value}:`,
                                    error
                                  );
                                  setIsLoading(false);
                                });
                            }
                          }}
                          className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#f36812]/20 focus:border-[#f36812] bg-gray-50"
                        >
                          <option value="all">None</option>
                          {companies.map((company) => (
                            <option
                              key={company.brand_id}
                              value={company.brand_id}
                            >
                              {company.brand_name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Rating Filter */}
                      <div className="space-y-3">
                        <label className="block text-sm font-medium text-gray-700">
                          Rating
                        </label>
                        <select
                          value={filters.rating}
                          onChange={(e) =>
                            updateFilter("rating", Number(e.target.value))
                          }
                          className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#f36812]/20 focus:border-[#f36812] bg-gray-50"
                        >
                          {filterOptions.ratings.map((rating) => (
                            <option key={rating.value} value={rating.value}>
                              {rating.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Toggles */}
                      <div className="space-y-4 pt-2">
                        {/* Discount Toggle */}
                        <label className="flex items-center gap-3 cursor-pointer group">
                          <div className="relative">
                            <input
                              type="checkbox"
                              checked={filters.showDiscount}
                              onChange={(e) =>
                                updateFilter("showDiscount", e.target.checked)
                              }
                              className="sr-only"
                            />
                            <div
                              className={`w-10 h-5 rounded-full transition ${filters.showDiscount
                                ? "bg-[#f36812]"
                                : "bg-gray-300"
                                }`}
                            ></div>
                            <div
                              className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform transform ${filters.showDiscount ? "translate-x-5" : ""
                                }`}
                            ></div>
                          </div>
                          <span className="text-sm text-gray-700 group-hover:text-[#f36812] transition">
                            Sale Items Only
                          </span>
                        </label>

                        {/* Stock Toggle */}
                        <label className="flex items-center gap-3 cursor-pointer group">
                          <div className="relative">
                            <input
                              type="checkbox"
                              checked={filters.stockOnly}
                              onChange={(e) =>
                                updateFilter("stockOnly", e.target.checked)
                              }
                              className="sr-only"
                            />
                            <div
                              className={`w-10 h-5 rounded-full transition ${filters.stockOnly
                                ? "bg-[#f36812]"
                                : "bg-gray-300"
                                }`}
                            ></div>
                            <div
                              className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform transform ${filters.stockOnly ? "translate-x-5" : ""
                                }`}
                            ></div>
                          </div>
                          <span className="text-sm text-gray-700 group-hover:text-[#f36812] transition">
                            In Stock Only
                          </span>
                        </label>
                      </div>

                      {/* Reset Button */}
                      <button
                        onClick={resetFilters}
                        className="w-full py-3 mt-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
                      >
                        Reset All Filters
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>

          {/* Products Section */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="md:w-3/4"
          >
            {/* Sort Controls */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 bg-white p-4 rounded-xl shadow-sm">
              <p className="text-gray-600 mb-3 sm:mb-0">
                <span className="font-medium text-gray-800">
                  {filteredProducts.length}
                </span>{" "}
                products found
              </p>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">Sort by:</span>
                <select
                  value={filters.sort}
                  onChange={(e) => updateFilter("sort", e.target.value)}
                  className="p-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#f36812]/20 focus:border-[#f36812] bg-gray-50"
                >
                  <option value="">Featured</option>
                  <option value="lowToHigh">Price: Low to High</option>
                  <option value="highToLow">Price: High to Low</option>
                </select>
              </div>
            </div>

            {/* Products Grid */}
            {paginatedProducts.length > 0 ? (
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="show"
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
              >
                {paginatedProducts.map((product) => {

                  return (
                    <motion.div
                      key={product.id}
                      variants={itemVariants}

                      layout
                      whileHover={{ y: -5 }}
                      className="bg-white rounded-xl shadow-lg overflow-hidden group cursor-pointer relative"
                      onClick={() => navigate(`/product/${product.product_id}`)}
                    >
                      {/* Product Image with Overlay */}
                      <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
                        <img
                          src={`${process.env.REACT_APP_API_URI}/uploads/${product.image}`}
                          alt={product.product_name}
                          className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                        />

                        {/* Discount Badge */}
                        {product.discount_price && (
                          <div className="absolute top-4 left-4 bg-[#f36812] text-white px-3 py-1 rounded-full text-sm font-medium">
                            Sale
                          </div>
                        )}

                        {/* Wishlist Button */}
                        <button
                          onClick={(e) => toggleWishlist(e, product.id)}
                          className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-white shadow-md transition-transform transform hover:scale-110 z-10"
                        >
                          <FaHeart
                            className={`${wishlist.includes(product.id)
                              ? "text-[#f36812]"
                              : "text-gray-300"
                              }`}
                          />
                        </button>

                        {/* Quick View Overlay */}
                        <div className="absolute inset-0 bg-black bg-opacity-20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                          <span className="bg-white text-gray-800 px-4 py-2 rounded-lg font-medium transform -translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                            Quick View
                          </span>
                        </div>
                      </div>

                      {/* Product Details */}
                      <div className="p-6">
                        {/* Brand */}
                        <p className="text-sm text-[#f36812] font-medium mb-1">
                          {product.brand_name || "Premium Brand"}
                        </p>

                        {/* Product Name */}
                        <h3 className="text-lg font-semibold text-gray-800 mb-2 group-hover:text-[#f36812] transition-colors line-clamp-1">
                          {product.product_name}
                        </h3>

                        {/* Description */}
                        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                          {product.description ||
                            "Premium quality product with exceptional craftsmanship."}
                        </p>

                        <div className="flex items-center justify-between mb-3">
                          {/* Rating */}
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <FaStar
                                key={i}
                                className={`w-4 h-4 ${i < (product.rating || 5)
                                  ? "text-[#f36812]"
                                  : "text-gray-300"
                                  }`}
                              />
                            ))}
                          </div>

                          {/* Stock Status */}
                          <span
                            className={`text-xs px-2 py-1 rounded-full ${product.in_stock
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                              }`}
                          >
                            {/* {product.in_stock ? "In Stock" : "Out of Stock"} */}
                            In Stock
                          </span>
                        </div>

                        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                          {/* Price */}
                          <div className="flex items-baseline gap-2">
                            <span className="text-2xl font-bold text-gray-800">
                              {currencySymbol} {formatPrice(convertPrice(product?.price).toFixed(2))}
                            </span>
                            {

                              product.price && (
                                <span className="text-sm text-gray-500 line-through">
                                  {currencySymbol} {formatPrice(convertPrice(product?.price).toFixed(2))}
                                </span>
                              )}
                          </div>

                          {/* Cart Button */}
                          <button
                            className="p-2 rounded-full bg-gray-100 text-[#f36812] hover:bg-[#f36812] hover:text-white transition-colors duration-200"
                            onClick={() =>
                              navigate(`/product/${product.product_id}`)
                            }
                          >
                            <FaShoppingCart className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )
                })}
              </motion.div>
            ) : (
              <div className="bg-white rounded-xl p-10 text-center shadow-lg">
                <img
                  src="https://www.svgrepo.com/show/529454/cart-large.svg"
                  alt="No results"
                  className="w-32 h-32 mx-auto mb-6 opacity-50"
                />
                <h3 className="text-2xl font-semibold text-gray-800 mb-2">
                  No Products Found
                </h3>
                <p className="text-gray-600 mb-6">
                  Try adjusting your search or filter criteria
                </p>
                <button
                  onClick={resetFilters}
                  className="px-6 py-3 bg-[#f36812] text-white font-medium rounded-lg hover:bg-[#e35702] transition-colors"
                >
                  Reset Filters
                </button>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-12 flex justify-center">
                <div className="flex items-center space-x-2">
                  {/* Prev Button */}
                  <button
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className={`w-10 h-10 flex items-center justify-center rounded-lg ${currentPage === 1
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-600 hover:bg-gray-100"
                      }`}
                  >
                    &lt;
                  </button>

                  {/* Page Buttons - only 5 at a time */}
                  {(() => {
                    let pages = [];
                    let start = Math.max(currentPage - 2, 1);
                    let end = Math.min(start + 4, totalPages);

                    // Adjust start again if we’re near the end
                    start = Math.max(end - 4, 1);

                    for (let i = start; i <= end; i++) {
                      pages.push(i);
                    }

                    return pages.map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-10 h-10 flex items-center justify-center rounded-lg ${currentPage === page
                          ? "bg-[#f36812] text-white"
                          : "bg-white text-gray-600 hover:bg-gray-100"
                          }`}
                      >
                        {page}
                      </button>
                    ));
                  })()}

                  {/* Next Button */}
                  <button
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className={`w-10 h-10 flex items-center justify-center rounded-lg ${currentPage === totalPages
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-600 hover:bg-gray-100"
                      }`}
                  >
                    &gt;
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Products;
